import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8090/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use((config) => {
  const tokenKey = process.env.REACT_APP_TOKEN_KEY || 'client_token';
  const token = localStorage.getItem(tokenKey);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface BoxUploadResult {
  success: boolean;
  message?: string;
  error?: string;
}

export interface BoxFile {
  id: string;
  name: string;
  fileSizeInKB: number;
  creationDate: string;
  creator: string;
}

export interface BoxFilesResponse {
  success: boolean;
  files: BoxFile[];
  totalFiles: number;
  error?: string;
}

export interface BoxFileRequestResult {
  success: boolean;
  url?: string;
  error?: string;
}

class BoxService {
  /**
   * Convert file to base64 string
   */
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix (e.g., "data:image/png;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  }

  async uploadFile(
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<BoxUploadResult> {
    try {
      console.log(`📦 Uploading file to Box: ${file.name} (${file.size} bytes)`);

      // Convert file to base64
      if (onProgress) onProgress(10);
      const fileContent = await this.fileToBase64(file);

      const payload = {
        fileName: file.name,
        fileContent: fileContent,
        fileType: file.type,
      };

      if (onProgress) onProgress(20);

      const response = await api.post('/box/upload', payload, {
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const backendProgress = Math.round((progressEvent.loaded / progressEvent.total) * 80);
            onProgress(20 + backendProgress);
          }
        }
      });

      const data = response.data;
      if (data && data.success) {
        if (onProgress) onProgress(100);
        return {
          success: true,
          message: data.message || 'File uploaded successfully'
        };
      }

      return {
        success: false,
        error: data?.error || 'Box upload failed'
      };
    } catch (error: any) {
      console.error('❌ Box upload error:', error);
      if (error.response) {
        return {
          success: false,
          error: `Server error: ${error.response.status} - ${error.response.data?.error || error.message}`
        };
      }
      if (error.request) {
        return {
          success: false,
          error: `Network error: No response from server - ${error.message}`
        };
      }
      return {
        success: false,
        error: `Upload failed: ${error.message}`
      };
    }
  }

  async getFiles(folderId?: string): Promise<BoxFilesResponse> {
    try {
      const query = folderId ? `?folderId=${encodeURIComponent(folderId)}` : '';
      const response = await api.get(`/box/files${query}`);
      if (response.data && response.data.success) {
        return {
          success: true,
          files: response.data.files || [],
          totalFiles: response.data.totalFiles || 0
        };
      }
      return {
        success: false,
        files: [],
        totalFiles: 0,
        error: response.data?.error || 'Failed to fetch files'
      };
    } catch (error: any) {
      if (error.response) {
        return {
          success: false,
          files: [],
          totalFiles: 0,
          error: `Server error: ${error.response.status} - ${error.response.data?.error || error.message}`
        };
      }
      if (error.request) {
        return {
          success: false,
          files: [],
          totalFiles: 0,
          error: `Network error: No response from server - ${error.message}`
        };
      }
      return {
        success: false,
        files: [],
        totalFiles: 0,
        error: `Get files failed: ${error.message}`
      };
    }
  }

  async uploadFileChunked(
    file: File,
    redRockId: string,
    onProgress?: (progress: number) => void
  ): Promise<BoxUploadResult> {
    try {
      console.log(`📦 Uploading file to Box (chunked): ${file.name} (${file.size} bytes)`);
      const MIN_CHUNKED_SIZE = 20000000;

      if (file.size < MIN_CHUNKED_SIZE) {
        if (onProgress) onProgress(10);
        const fileContent = await this.fileToBase64(file);
        const directResponse = await api.post('/box/upload-session-chunked', {
          fileName: file.name,
          fileSize: file.size,
          redRockId,
          fileContent,
          fileType: file.type,
        });
        const directData = directResponse.data;
        if (directData && directData.success && directData.directUpload) {
          if (onProgress) onProgress(100);
          return {
            success: true,
            message: directData.message || 'File uploaded successfully'
          };
        }
        return {
          success: false,
          error: directData?.error || 'Failed to upload small file'
        };
      }

      const sha1Base64 = async (buffer: ArrayBuffer): Promise<string> => {
        const hash = await crypto.subtle.digest('SHA-1', buffer);
        const bytes = new Uint8Array(hash);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
      };

      const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
      };

      if (onProgress) onProgress(5);

      const sessionResponse = await api.post('/box/upload-session-chunked', {
        fileName: file.name,
        fileSize: file.size,
        redRockId,
      });

      const sessionData = sessionResponse.data;
      if (!sessionData || !sessionData.success || !sessionData.session) {
        return {
          success: false,
          error: sessionData?.error || 'Failed to start upload session'
        };
      }

      const partSize: number = sessionData.session.part_size;
      const uploadUrl: string = sessionData.session.upload_part;
      const commitUrl: string = sessionData.session.commit;

      const parts: Array<{ part_id: string; offset: number; size: number }> = [];
      let uploadedBytes = 0;

      for (let offset = 0; offset < file.size; offset += partSize) {
        const end = Math.min(offset + partSize, file.size);
        const chunk = file.slice(offset, end);
        const chunkBuffer = await chunk.arrayBuffer();
        const chunkDigest = await sha1Base64(chunkBuffer);
        const chunkBase64 = arrayBufferToBase64(chunkBuffer);

        const partResponse = await api.post('/box/upload-part-chunked', {
          uploadUrl,
          totalSize: file.size,
          offset,
          chunkSize: end - offset,
          digest: chunkDigest,
          contentBase64: chunkBase64
        });

        const partData = partResponse.data;
        if (!partData || !partData.success || !partData.part) {
          return {
            success: false,
            error: partData?.error || 'Failed to upload chunk'
          };
        }

        parts.push(partData.part);
        uploadedBytes = end;
        if (onProgress) {
          const progress = Math.round((uploadedBytes / file.size) * 80) + 10;
          onProgress(progress);
        }
      }

      const fileBuffer = await file.arrayBuffer();
      const fileDigest = await sha1Base64(fileBuffer);

      const commitResponse = await api.post('/box/upload-commit-chunked', {
        commitUrl,
        fileSha1: fileDigest,
        parts
      });

      const commitData = commitResponse.data;
      if (commitData && commitData.success) {
        if (onProgress) onProgress(100);
        return {
          success: true,
          message: 'File uploaded successfully'
        };
      }

      return {
        success: false,
        error: commitData?.error || 'Failed to commit upload'
      };
    } catch (error: any) {
      console.error('❌ Box chunked upload error:', error);
      if (error.response) {
        return {
          success: false,
          error: `Server error: ${error.response.status} - ${error.response.data?.error || error.message}`
        };
      }
      if (error.request) {
        return {
          success: false,
          error: `Network error: No response from server - ${error.message}`
        };
      }
      return {
        success: false,
        error: `Upload failed: ${error.message}`
      };
    }
  }

  async createFileRequest(
    redRockId: string,
    options?: {
      title?: string;
      description?: string;
      status?: 'active' | 'inactive';
      isEmailRequired?: boolean;
      isDescriptionRequired?: boolean;
      expiresAt?: string;
    }
  ): Promise<BoxFileRequestResult> {
    const templateFileRequestId =
      process.env.REACT_APP_BOX_FILE_REQUEST_TEMPLATE_ID || '20758307707';
    if (!templateFileRequestId) {
      return {
        success: false,
        error: 'Missing Box file request template id'
      };
    }

    try {
      const payload = {
        redRockId,
        templateFileRequestId,
        ...options
      };
      const response = await api.post('/box/file-request', payload);
      const data = response.data;
      if (data && data.success && data.url) {
        return {
          success: true,
          url: data.url
        };
      }
      return {
        success: false,
        error: data?.error || 'Failed to create file request'
      };
    } catch (error: any) {
      if (error.response) {
        return {
          success: false,
          error: `Server error: ${error.response.status} - ${error.response.data?.error || error.message}`
        };
      }
      if (error.request) {
        return {
          success: false,
          error: `Network error: No response from server - ${error.message}`
        };
      }
      return {
        success: false,
        error: `File request failed: ${error.message}`
      };
    }
  }
}

const boxService = new BoxService();
export default boxService;
